import { useEffect, useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import type { TextStyleMixin } from '@elfsight/widget-ui';
import { Button, textColorMixin, textStyleMixin } from '@elfsight/widget-ui';
import {
  type FourValueTupleConvertable,
  getInvertedColor
} from '@elfsight/widget-utils';
import type { FormField } from '../contexts';
import { useFormSelector, useWidgetDispatch } from '../contexts';
import { formattingText } from './formattingText';
import { submitMessageSettings } from './submitMessageSettings';
import { useFormReset } from './useFormReset';

export interface SubmitMessageProps extends SubmitMessageButtonProps {
  isHorizontal?: boolean;
  submitMessageTitle?: string;
  submitMessageText?: string;
  submitMessageAlignment?: SubmitMessageAlignment;
  submitMessageTitleColor?: string;
  submitMessageTextColor?: string;
  submitMessageTitleFontStyle?: TextStyleMixin;
  submitMessageTextFontStyle?: TextStyleMixin;
  submitMessageButton?: boolean;
  submitMessageButtonStyle?: CSSProperties;
  gap?: number;
}

type SubmitMessageButtonProps = {
  fields: FormField[];
  submitMessageButtonBorderRadius?: FourValueTupleConvertable<number | string>;
  submitMessageButtonText?: string;
  submitMessageButtonTextFontStyle?: TextStyleValue;
  submitMessageButtonColor?: string;
  submitMessageButtonStyle?: CSSProperties;
  onSubmitMessageButtonClick?: () => void;
  className?: string;
  fullWidth?: boolean;
};

export function SubmitMessage({
  fields,
  className,
  isHorizontal = false,
  submitMessageTitle = submitMessageSettings.submitMessageTitle,
  submitMessageText = submitMessageSettings.submitMessageTitle,
  submitMessageAlignment = submitMessageSettings.submitMessageAlignment,
  submitMessageTitleColor = submitMessageSettings.submitMessageTitleColor,
  submitMessageTextColor = submitMessageSettings.submitMessageTextColor,
  submitMessageTitleFontStyle = submitMessageSettings.submitMessageTitleFontStyle as TextStyleMixin,
  submitMessageTextFontStyle = submitMessageSettings.submitMessageTextFontStyle as TextStyleMixin,
  submitMessageButton = submitMessageSettings.submitMessageButton,
  submitMessageButtonStyle,
  fullWidth,
  gap,
  ...forwardingProps
}: SubmitMessageProps) {
  const { fields: changedFields } = useFormSelector();

  const messageAlignment = isHorizontal ? 'left' : submitMessageAlignment;
  const alignItems = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end'
  }[messageAlignment];

  return (
    <Container className={className}>
      <Message
        style={{
          textAlign: messageAlignment,
          alignItems,
          height: '100%',
          gap: gap ?? (isHorizontal ? 8 : 20),
          width: fullWidth ? '100%' : undefined
        }}
      >
        {!!submitMessageTitle && (
          <Title
            _textStyle={submitMessageTitleFontStyle}
            _textColor={submitMessageTitleColor}
            dangerouslySetInnerHTML={{
              __html: formattingText(submitMessageTitle, fields, changedFields)
            }}
          />
        )}
        {!!submitMessageText && (
          <Text
            _textStyle={submitMessageTextFontStyle}
            _textColor={submitMessageTextColor}
            dangerouslySetInnerHTML={{
              __html: formattingText(submitMessageText, fields, changedFields)
            }}
          />
        )}
        {submitMessageButton && !isHorizontal && (
          <SubmitMessageButton
            fields={fields}
            fullWidth={fullWidth}
            submitMessageButtonStyle={submitMessageButtonStyle}
            {...forwardingProps}
          />
        )}
      </Message>
      {submitMessageButton && isHorizontal && (
        <SubmitMessageButton
          fields={fields}
          fullWidth={fullWidth}
          {...forwardingProps}
        />
      )}
    </Container>
  );
}

function SubmitMessageButton({
  fields,
  submitMessageButtonBorderRadius,
  submitMessageButtonText = submitMessageSettings.submitMessageButtonText,
  submitMessageButtonColor = submitMessageSettings.submitMessageButtonColor,
  submitMessageButtonTextFontStyle = submitMessageSettings.submitMessageButtonTextFontStyle,
  submitMessageButtonStyle,
  onSubmitMessageButtonClick,
  fullWidth = false
}: SubmitMessageButtonProps) {
  const dispatch = useWidgetDispatch();
  const resetForm = useFormReset(fields);

  const [submitMessageButtonFontColor, setSubmitMessageButtonFontColor] =
    useState(getInvertedColor(submitMessageButtonColor));
  useEffect(() => {
    setSubmitMessageButtonFontColor(getInvertedColor(submitMessageButtonColor));
  }, [submitMessageButtonColor]);

  const handleButtonClick = () => {
    dispatch({
      type: 'resetWidget'
    });
    resetForm();
    onSubmitMessageButtonClick?.();
  };

  return (
    <Button
      style={submitMessageButtonStyle}
      text={submitMessageButtonText}
      color={submitMessageButtonColor}
      borderRadius={submitMessageButtonBorderRadius}
      fontColor={submitMessageButtonFontColor}
      font={submitMessageButtonTextFontStyle}
      onClick={handleButtonClick}
      fullWidth={fullWidth}
    />
  );
}

export type SubmitMessageAlignment = 'left' | 'center' | 'right';

interface TextStyle {
  _textStyle: TextStyleMixin;
  _textColor: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const Message = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Title = styled.div<TextStyle>`
  line-height: 1.3;
  ${textStyleMixin}
  ${textColorMixin}
`;

const Text = styled.div<TextStyle>`
  line-height: 1.2;
  ${textStyleMixin}
  ${textColorMixin}
`;
